import React, { useState, useEffect } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { getCrrAuthHeaders } from './getOidcToken';

// Configuration MSAL via les variables d'environnement (préfixées par REACT_APP_)
const msalConfig = {
	auth: {
		clientId: window._env_.CLIENT_ID,
		authority: `https://login.microsoftonline.com/` + window._env_.TENANT_ID,
		redirectUri: window.location.origin
	}
};

// Scopes délégués requis
const graphScopes = [
	"User.Read",
	"Calendars.Read",
	"OnlineMeetingRecording.Read.All",
	"Files.Read.All"
];

const msalInstance = new PublicClientApplication(msalConfig);

// Récupère les événements du calendrier pour une date donnée (entre 06:00 et 23:00)
async function fetchCalendarEvents(accessToken, selectedDate) {
	// Construction des dates de début (lundi) et de fin (dimanche) de la semaine à partir de la date sélectionnée
	const selected = new Date(selectedDate);
	const dayOfWeek = selected.getDay();
	const startOfWeek = new Date(selected);
	startOfWeek.setDate(selected.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Si dimanche, reculer de 6 jours
	startOfWeek.setHours(0, 0, 0, 0); // Début de la journée (00:00:00)

	const endOfWeek = new Date(startOfWeek);
	endOfWeek.setDate(startOfWeek.getDate() + 6); // Ajouter 6 jours pour obtenir le dimanche
	endOfWeek.setHours(23, 59, 59, 999); // Fin de la journée (23:59:59)

	const response = await fetch(
		`https://graph.microsoft.com/v1.0/me/calendarview?startDateTime=${encodeURIComponent(startOfWeek.toISOString())}&endDateTime=${encodeURIComponent(endOfWeek.toISOString())}&$top=100`,
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Prefer: 'outlook.timezone="UTC"'
			}
		}
	);
	if (!response.ok) {
		throw new Error(`Erreur Graph API (CalendarView): ${response.statusText}`);
	}
	const data = await response.json();
	console.log(data.value);
	return data.value;
}

// Récupère les fichiers du dossier "Enregistrements" de OneDrive
async function fetchRecordings(accessToken) {
	// Premier essai avec le dossier recordings
	let response = await fetch(
		'https://graph.microsoft.com/v1.0/me/drive/special/recordings/children',
		{
			headers: {
				Authorization: `Bearer ${accessToken}`
			}
		}
	);

	// Si 404, essayer avec le dossier meetings
	if (response.status === 404) {
		console.log("Dossier recordings non trouvé, tentative avec le dossier meetings...");
		response = await fetch(
			'https://graph.microsoft.com/v1.0/me/drive/special/meetings/children',
			{
				headers: {
					Authorization: `Bearer ${accessToken}`
				}
			}
		);
	}

	if (!response.ok) {
		throw new Error(`Erreur Graph API (Files): ${response.statusText}`);
	}

	const data = await response.json();
	console.log("files in Documents", data.value);
	return data.value;
}

// Tente d'associer un événement à un fichier d'enregistrement en comparant le sujet et la date
function matchRecordingToEvent(event, recordings) {
	if (!event.iCalUId) return null; // Sécurité : si pas d'iCalUId, on ne peut pas matcher

	const eventIcalUid = event.iCalUId;

	return recordings.find(file => file.source?.iCalUid === eventIcalUid) || null;
}

function App() {
	// Initialiser la date sélectionnée à aujourd'hui (format YYYY-MM-DD)
	const today = new Date().toISOString().slice(0, 10);
	const [selectedDate, setSelectedDate] = useState(today);
	const [account, setAccount] = useState(null);
	const [matchedMeetings, setMatchedMeetings] = useState([]);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingRequest, setLoadingRequest] = useState(false);
	const [inputValue, setInputValue] = useState(null);

	// Connexion via loginPopup
	const signIn = async () => {
		if (isLoading) return;
		setIsLoading(true);
		try {
			const loginResponse = await msalInstance.loginPopup({ scopes: graphScopes });
			setAccount(loginResponse.account);
		} catch (err) {
			console.error("Erreur lors du loginPopup:", err);
			setError(err.message);
		} finally {
			setIsLoading(false);
		}
	};

	// Fonction principale qui récupère les événements et les fichiers, puis associe les réunions à leurs enregistrements
	const acquireTokenAndFetchData = async () => {
		try {
			const tokenResponse = await msalInstance.acquireTokenSilent({
				scopes: graphScopes,
				account: account
			});
			const accessToken = tokenResponse.accessToken;

			const [events, files] = await Promise.all([
				fetchCalendarEvents(accessToken, selectedDate),
				fetchRecordings(accessToken)
			]);

			const matched = events.map(event => {
				const recordingFile = matchRecordingToEvent(event, files);
				return { event, recording: recordingFile };
			});
			setMatchedMeetings(matched);
			console.log("matched", matched);
		} catch (err) {
			console.error("Erreur lors de la récupération des données:", err);
			setError(err.message);
		}
	};

	const formatDate = (isoDate) => {
		const date = new Date(isoDate);
		return date.toLocaleDateString('fr-FR', {
			weekday: 'long', // lundi, mardi…
			year: 'numeric',
			month: 'long',   // janvier, février…
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const handleFormSubmit = async (e) => {
		e.preventDefault();
		console.log(inputValue)
		if (inputValue == "")
			return;
		const authHeaders = await getCrrAuthHeaders(msalInstance, account, graphScopes);
		await fetch(window._env_.API_URL_CRR+"/crr/feedback", {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				...authHeaders,
			},
			body: new URLSearchParams({
				report_html: inputValue,
				sender: account?.username || "utilisateur_inconnu",
			}),
		})
		setInputValue("")
		alert("Merci pour votre retour !")
	}

	async function handleSendCR(recording, id, attendees, organizer, date, title) {
		try {
			setLoadingRequest(true);
			const formData = new FormData();
			formData.append('file_url', recording);
			console.log(attendees.filter(participant => participant.status?.response === "accepted"));
			formData.append('attendees', JSON.stringify(attendees));
			formData.append('organizer', JSON.stringify(organizer));
			formData.append('date', formatDate(date));
			formData.append('title', JSON.stringify(title));

		const authHeaders = await getCrrAuthHeaders(msalInstance, account, graphScopes, { iCalUId: id });
		const response = await fetch(window._env_.API_URL_CRR + "/crr/audio", {
			method: 'POST',
			headers: authHeaders,
			body: formData
		});

			if (response.status === 202 || response.status === 204 || response.status === 200) {
				alert('Requête envoyée avec succès\nVeuillez patienter pendant le traitement du meeting, votre CR sera envoyé sur votre boite mail dans quelques minutes.\nInutile de rester sur cette page.');
				let taskId = await response.json();
				taskId = taskId.task_id;
				console.log(taskId);
				// for (let i = 0; i < 60; i++) {
				// 	await new Promise(resolve => setTimeout(resolve, 10000))
				// 	console.log('Polling... ⏳');

				// 	let pol = await fetch(window._env_.API_URL_CRR + "/tasks/" + taskId, {
				// 		method: 'GET',
				// 		headers: {
				// 			'Authorization': 'Bearer ma-cle-api'
				// 		},
				// 	});
				// 	if (pol.status === 200) {
				// 		let data = await pol.json();
				// 		if (data.status === 'pending')
				// 			continue;
				// 		else if (data.status === 'completed') {
				// 			console.log(data.text.transcription);
				// 			alert('CR généré avec succès !');
				// 			break;
				// 		}
				// 		else {
				// 			alert('Erreur lors de la génération du CR');
				// 			break;
				// 		}
				// 	}
				// }
			}
			else
				alert('Erreur lors de l\'envoi de la requête');
			setLoadingRequest(false);

		} catch (error) {
			console.error('Erreur:', error);
			setLoadingRequest(false);
		}
	}

	// Recharge les données quand le compte ou la date sélectionnée change
	useEffect(() => {
		if (account) {
			acquireTokenAndFetchData();
		}
	}, [account, selectedDate]);

	return (
		<div style={{
			minHeight: '100vh',
			backgroundColor: '#f5f7fa',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			padding: '2rem',
			fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
		}}>
			<h1 style={{
				fontSize: '2rem',
				fontWeight: '600',
				color: '#222',
				marginBottom: '1.5rem',
				position: 'relative',
				paddingBottom: '0.5rem',
			}}>
				Mes Réunions Teams Enregistrées
				<span style={{
					position: 'absolute',
					bottom: 0,
					left: '50%',
					transform: 'translateX(-50%)',
					width: '80px',
					height: '4px',
					backgroundColor: '#009fe3',
					borderRadius: '2px',
				}} />
			</h1>

			{account && (
				<div style={{
					width: '100%',
					maxWidth: '400px',
					marginBottom: '2rem',
				}}>
					<label htmlFor="date-select" style={{
						display: 'block',
						marginBottom: '0.5rem',
						fontWeight: '500',
						color: '#555',
					}}>
						Sélectionnez une semaine
					</label>
					<div style={{ position: 'relative' }}>
						<span style={{
							position: 'absolute',
							top: '50%',
							left: '1rem',
							transform: 'translateY(-50%)',
							pointerEvents: 'none',
							fontSize: '1.2rem',
							color: '#aaa',
						}}>
							📅
						</span>
						<input
							id="date-select"
							type="date"
							value={selectedDate}
							onChange={e => setSelectedDate(e.target.value)}
							style={{
								width: '100%',
								padding: '0.75rem 1rem 0.75rem 2.5rem',
								borderRadius: '30px',
								border: 'none',
								boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
								fontSize: '1rem',
								transition: 'box-shadow 0.2s ease, transform 0.2s ease',
								cursor: 'pointer',
							}}
							onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(0,159,227,0.5)'}
							onBlur={e => e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'}
						/>
					</div>
				</div>
			)}

			{!account ? (
				<button
					onClick={signIn}
					disabled={isLoading}
					style={{
						backgroundColor: '#009fe3',
						color: '#fff',
						padding: '0.75rem 2rem',
						border: 'none',
						borderRadius: '30px',
						fontSize: '1rem',
						fontWeight: '500',
						cursor: 'pointer',
						boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
						transition: 'background-color 0.2s',
					}}
					onMouseEnter={e => e.target.style.backgroundColor = '#007bb5'}
					onMouseLeave={e => e.target.style.backgroundColor = '#009fe3'}
				>
					{isLoading ? 'Connexion en cours…' : 'Se connecter avec Microsoft'}
				</button>
			) : (
				<div style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'flex-start',
					gap: '2rem',
					flexWrap: 'wrap',
					width: '100%',
					maxWidth: '1000px',
				}}>
					{/* Section Réunions */}
					<div style={{ flex: 2, minWidth: '300px' }}>
						<p style={{ textAlign: 'center', color: '#666', marginBottom: '1rem' }}>
							Connecté en tant que <strong style={{ color: '#222' }}>{account.username}</strong>
						</p>

						{matchedMeetings.filter(({ recording }) => recording).length === 0 ? (
							<p style={{ textAlign: 'center', color: '#999' }}>
								Aucune réunion trouvée pour la semaine sélectionnée.
							</p>
						) : (
							<div style={{
								display: 'grid',
								gridTemplateColumns: '1fr',
								gap: '1rem',
							}}>
								{matchedMeetings.filter(({ recording }) => recording).map(({ event, recording }) => (
									<div
										key={event.id}
										style={{
											backgroundColor: '#fff',
											padding: '1rem',
											borderRadius: '8px',
											borderLeft: '4px solid #009fe3',
											boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
											transition: 'transform 0.2s ease, box-shadow 0.2s ease',
											cursor: 'pointer',
										}}
										onMouseEnter={e => {
											e.currentTarget.style.transform = 'translateY(-4px)';
											e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
										}}
										onMouseLeave={e => {
											e.currentTarget.style.transform = 'translateY(0)';
											e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
										}}
									>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
											<h2 style={{ margin: 0, fontSize: '1.125rem', color: '#222' }}>
												{event.subject || 'Sans objet'}
											</h2>
											{loadingRequest ? (
												<span style={{ color: '#009fe3' }}>Chargement…</span>
											) : (
												<button
													onClick={async () => {
														await handleSendCR(
															recording['@microsoft.graph.downloadUrl'],
															recording.source.iCalUid,
															event.attendees,
															event.organizer,
															event.start.dateTime,
															event.subject
														);
													}}
													style={{
														background: 'none',
														border: 'none',
														color: '#009fe3',
														fontWeight: '500',
														cursor: 'pointer',
													}}
												>
													Générer un CR
												</button>
											)}
										</div>
										<p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#555' }}>
											De {new Date(event.start.dateTime).toLocaleString()} à {new Date(event.end.dateTime).toLocaleString()}
										</p>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Formulaire à droite */}
					<div style={{
						flex: '0 0 220px',
						backgroundColor: '#fff',
						padding: '1rem',
						borderRadius: '6px',
						boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
						fontSize: '0.875rem',
						boxSizing: 'border-box',
					}}>
						<h3 style={{
							marginTop: 0,
							marginBottom: '0.75rem',
							fontSize: '1rem',
							color: '#222',
						}}>Feedback</h3>
						<form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
							<textarea
								placeholder="Votre message..."
								value={inputValue}
								onChange={e => setInputValue(e.target.value)}
								rows={4}
								style={{
									width: '100%',
									resize: 'none',
									padding: '0.75rem',
									marginBottom: '0.5rem',
									borderRadius: '12px',
									border: '1px solid #ccc',
									fontSize: '0.875rem',
									boxSizing: 'border-box',
									fontFamily: 'inherit',
								}}
							/>
							<button
								type="submit"
								style={{
									width: '100%',
									padding: '0.5rem',
									backgroundColor: '#009fe3',
									color: '#fff',
									border: 'none',
									borderRadius: '20px',
									fontWeight: '500',
									cursor: 'pointer',
									fontSize: '0.875rem',
								}}
							>
								Envoyer
							</button>
						</form>
					</div>
				</div>
			)}

			{error && <p style={{ color: 'red', marginTop: '2rem' }}>Erreur : {error}</p>}
		</div>

	);
}

export default App;
